import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  LocalPharmacy as RxIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  Visibility as VisibilityIcon,
  Article as ArticleIcon,
  Summarize as MedicalReportIcon,
  ReportProblem as RiskIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import ClinicalHtmlEditor, { clinicalBodyIsEmpty } from '../clinic/ClinicalHtmlEditor';
import ModelTemplateSearchField from '../clinic/ModelTemplateSearchField';
import PrescriptionTemplateChipPicker from '../clinic/PrescriptionTemplateChipPicker';
import type { EvolutionAttachedPrintable, EvolutionPrintableCategory } from './evolutionWorkflowTypes';
import PrescriptionFormDialog, { type PrescriptionFormSuccessResult } from '../clinic/PrescriptionFormDialog';
import EvolutionPrescriptionsSendDialog from './EvolutionPrescriptionsSendDialog';
import PrescriptionBatchPrintDialog, {
  buildPrescriptionPrintQueueFromIds,
  type PrescriptionPrintQueueItem,
} from '../clinic/PrescriptionBatchPrintDialog';
import MemedPrescriptionButton from '../clinic/MemedPrescriptionButton';
import { buildMemedPrescriptionSummary } from '../../services/memedService';
import CertificateFormDialog from '../clinic/CertificateFormDialog';
import HealthcareMedicalReportDialog from '../HealthcareMedicalReportDialog';
import DocumentTextTemplateFormDialog from '../clinic/DocumentTextTemplateFormDialog';
import SortableModelList from './SortableModelList';
import GuidanceButton from '../common/GuidanceButton';
import OphthalmologyExamOrderSection from './OphthalmologyExamOrderSection';
import OphthalmologyRefractionSection from './OphthalmologyRefractionSection';
import {
  buildOphthalmologyExamOrderBody,
  emptyOphthalmologySelections,
  type OphthalmologyExamSelection,
} from './ophthalmologyExamOrderConstants';
import {
  emptyOphthalmologyRefraction,
  serializeRefractionBody,
  type OphthalmologyRefractionValues,
} from './ophthalmologyRefractionConstants';
import {
  buildExamOrderTemplateBodyText,
  formatExamOrderBodyWithoutNumbering,
} from '../../utils/buildExamOrderTemplateBodyText';
import { mergeExamOrderEditorBodies } from '../../utils/mergeExamOrderEditorBodies';
import {
  defaultEvolutionPrintableCategoryForSpecialty,
  filterEvolutionPrintableCategoriesForSpecialty,
} from '../../constants/fisioterapiaUi';

const CATEGORY_DEF: {
  key: EvolutionPrintableCategory;
  label: string;
  icon: React.ElementType;
}[] = [
  { key: 'prescription', label: 'Receituários', icon: RxIcon },
  { key: 'certificate', label: 'Atestados', icon: DescriptionIcon },
  { key: 'medical_report', label: 'Relatórios médicos', icon: MedicalReportIcon },
  { key: 'exam_order', label: 'Solicitações de Exames', icon: ScienceIcon },
  { key: 'refraction', label: 'Refração (óculos)', icon: VisibilityIcon },
  { key: 'report', label: 'Laudos', icon: ArticleIcon },
  { key: 'surgical_risk', label: 'Risco cirúrgico', icon: RiskIcon },
  { key: 'insurance_guide', label: 'Guias de convênio', icon: AssignmentIcon },
];

interface EvolutionPrintablesWizardProps {
  open: boolean;
  onClose: () => void;
  onCommit: (items: EvolutionAttachedPrintable | EvolutionAttachedPrintable[]) => void;
  /** Paciente / profissional da evolução (obrigatório para receituário e atestado completos). */
  patientId: string;
  professionalId: string;
  initialPatient?: any;
  initialProfessional?: any;
}

/** Assistente: categorias + modelos; receituário/atestado abrem o mesmo fluxo das páginas da clínica. */
const EvolutionPrintablesWizard: React.FC<EvolutionPrintablesWizardProps> = ({
  open,
  onClose,
  onCommit,
  patientId,
  professionalId,
  initialPatient,
  initialProfessional,
}) => {
  const [category, setCategory] = useState<EvolutionPrintableCategory>('prescription');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [editorSession, setEditorSession] = useState<{
    id?: string;
    name: string;
    body: string;
  } | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);

  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [signAndSendIds, setSignAndSendIds] = useState<string[]>([]);
  const [docTemplateDialogOpen, setDocTemplateDialogOpen] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [medicalReportDialogOpen, setMedicalReportDialogOpen] = useState(false);
  const [prefillRxTemplate, setPrefillRxTemplate] = useState<any | null>(null);
  const [prefillCertTemplate, setPrefillCertTemplate] = useState<any | null>(null);
  const [prefillMedicalReportTemplate, setPrefillMedicalReportTemplate] = useState<any | null>(null);

  const [clinicMedicalSpecialty, setClinicMedicalSpecialty] = useState<string | null>(null);
  const [clinicSettingsLoaded, setClinicSettingsLoaded] = useState(false);
  const [ophSelections, setOphSelections] = useState<OphthalmologyExamSelection>(() =>
    emptyOphthalmologySelections(),
  );
  const [ophOutros, setOphOutros] = useState('');
  const [ophObs, setOphObs] = useState('');
  const [refractionValues, setRefractionValues] = useState<OphthalmologyRefractionValues>(() =>
    emptyOphthalmologyRefraction(),
  );
  /** Itens prontos no assistente; só vão para a evolução ao confirmar no rodapé. */
  const [assistantQueue, setAssistantQueue] = useState<EvolutionAttachedPrintable[]>([]);
  const [examPickerRows, setExamPickerRows] = useState<any[]>([]);
  const [examPickerLoading, setExamPickerLoading] = useState(false);
  const [rxBatchPrintOpen, setRxBatchPrintOpen] = useState(false);
  const [rxBatchPrintQueue, setRxBatchPrintQueue] = useState<PrescriptionPrintQueueItem[]>([]);
  const [rxBatchPrintFirstVia, setRxBatchPrintFirstVia] = useState(false);

  const categoryLabel = (k: EvolutionPrintableCategory) =>
    CATEGORY_DEF.find((c) => c.key === k)?.label || k;

  const enqueueToAssistant = (printable: EvolutionAttachedPrintable) => {
    setAssistantQueue((prev) => [...prev, printable]);
  };

  const removeFromAssistantQueue = (clientKey: string) => {
    setAssistantQueue((prev) => prev.filter((e) => e.clientKey !== clientKey));
  };

  const commitPrintables = (items: EvolutionAttachedPrintable | EvolutionAttachedPrintable[]) => {
    const batch = Array.isArray(items) ? items : [items];
    if (batch.length) onCommit(batch);
  };

  const flushAssistantQueueAndClose = () => {
    setAssistantQueue((q) => {
      if (q.length) commitPrintables(q);
      return [];
    });
    onClose();
  };

  const discardWizardAndClose = () => {
    setAssistantQueue([]);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setClinicSettingsLoaded(false);
      return;
    }
    let cancelled = false;
    setClinicSettingsLoaded(false);
    axios
      .get(getApiUrl('/clinic/settings'))
      .then((res) => {
        if (cancelled) return;
        const v = res.data?.data?.clinic_medical_specialty;
        setClinicMedicalSpecialty(typeof v === 'string' && v.trim() ? v.trim() : null);
      })
      .catch(() => {
        if (!cancelled) setClinicMedicalSpecialty(null);
      })
      .finally(() => {
        if (!cancelled) setClinicSettingsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const loadTemplates = useCallback(async () => {
    if (!open) return;
    if (!clinicSettingsLoaded && (category === 'exam_order' || category === 'refraction')) return;
    setLoading(true);
    try {
      if (category === 'prescription' || category === 'certificate') {
        setRows([]);
        setLoading(false);
        return;
      } else if (category === 'refraction') {
        setRows([]);
        setLoading(false);
        return;
      } else if (category === 'exam_order') {
        setExamPickerLoading(true);
        try {
          const { data } = await axios.get(getApiUrl('/clinic/exam-orders/templates'));
          const list = Array.isArray(data?.data) ? data.data : data;
          setExamPickerRows(Array.isArray(list) ? list : []);
        } catch {
          setExamPickerRows([]);
        } finally {
          setExamPickerLoading(false);
        }
        setRows([]);
      } else if (category === 'insurance_guide') {
        const insId = initialPatient?.insurance_id || null;
        if (!insId) {
          setRows([]);
          setLoading(false);
          return;
        }
        const { data } = await axios.get(getApiUrl('/clinic/insurance-forms'), {
          params: { insurance_id: insId, search: search || undefined },
        });
        const list = Array.isArray(data?.data) ? data.data : [];
        setRows(list.map((f: any) => ({ ...f, isInsuranceForm: true })));
      } else if (category === 'report' || category === 'surgical_risk') {
        let saved: any[] = [];
        try {
          const { data } = await axios.get(getApiUrl('/clinic/document-text-templates'), {
            params: {
              category,
              professional_id: professionalId || undefined,
              search: search || undefined,
            },
          });
          saved = Array.isArray(data?.data) ? data.data : [];
        } catch {
          saved = [];
        }
        setRows([
          {
            id: `free-${category}`,
            name: `${category === 'report' ? 'Laudo' : 'Risco cirúrgico'} — texto livre`,
            isFree: true,
          },
          ...saved.map((t: any) => ({ ...t, isFree: false })),
        ]);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
    // Depende só de `insurance_id` (primitivo) — evita loop de re-render quando
    // `initialPatient` chega como novo objeto a cada render do componente pai.
  }, [open, clinicSettingsLoaded, category, search, clinicMedicalSpecialty, professionalId, initialPatient?.insurance_id]);

  useEffect(() => {
    const delay = category === 'report' || category === 'surgical_risk' ? 0 : 280;
    const t = setTimeout(() => {
      if (!open) return;
      if (category === 'exam_order' || category === 'refraction' ? clinicSettingsLoaded : true) loadTemplates();
    }, delay);
    return () => clearTimeout(t);
  }, [open, clinicSettingsLoaded, category, search, loadTemplates]);

  useEffect(() => {
    if (open) {
      setCategory(defaultEvolutionPrintableCategoryForSpecialty(clinicMedicalSpecialty));
      setSearch('');
      setEditorSession(null);
      setPrescriptionDialogOpen(false);
      setDocTemplateDialogOpen(false);
      setReorderMode(false);
      setCertificateDialogOpen(false);
      setMedicalReportDialogOpen(false);
      setPrefillRxTemplate(null);
      setPrefillCertTemplate(null);
      setPrefillMedicalReportTemplate(null);
      setOphSelections(emptyOphthalmologySelections());
      setOphOutros('');
      setOphObs('');
      setRefractionValues(emptyOphthalmologyRefraction());
      setAssistantQueue([]);
    }
  }, [open, clinicMedicalSpecialty]);

  useEffect(() => {
    setSearch('');
    setReorderMode(false);
  }, [category]);

  const visibleCategories = useMemo(
    () => filterEvolutionPrintableCategoriesForSpecialty(CATEGORY_DEF, clinicMedicalSpecialty),
    [clinicMedicalSpecialty],
  );

  useEffect(() => {
    if (!clinicSettingsLoaded) return;
    const allowed = visibleCategories.some((c) => c.key === category);
    if (!allowed) {
      setCategory(defaultEvolutionPrintableCategoryForSpecialty(clinicMedicalSpecialty));
    }
  }, [clinicSettingsLoaded, clinicMedicalSpecialty, category, visibleCategories]);

  const normalizeExamTemplateRow = (row: any) => {
    const items = row?.items ?? row?.clinic_exam_order_template_items ?? [];
    return {
      ...row,
      items: Array.isArray(items) ? items : [],
    };
  };

  const formatExamTemplateSecondary = (row: any): string | undefined => {
    if (row.isFree) return 'Digite a solicitação manualmente no editor';
    const tpl = normalizeExamTemplateRow(row);
    const items = tpl.items.filter((it: any) => String(it.exam_name || it.name || '').trim());
    if (!items.length) return 'Sem exames no modelo — cadastre em Pedidos de Exames';
    const preview = items
      .slice(0, 3)
      .map((it: any) => it.exam_name || it.name)
      .join(', ');
    const suffix = items.length > 3 ? '…' : '';
    return `${items.length} exame(s): ${preview}${suffix}`;
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    const matches = (r: any) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.title || '').toLowerCase().includes(q) ||
      String(r.code || '').toLowerCase().includes(q) ||
      (category === 'exam_order' && !r.isFree
        ? formatExamTemplateSecondary(r)?.toLowerCase().includes(q)
        : false);
    const freeRows = rows.filter((r) => r.isFree);
    const matched = rows.filter((r) => !r.isFree && matches(r));
    return [...freeRows, ...matched];
  }, [rows, search, category]);

  const hasClinicalContext = Boolean(patientId && professionalId);

  const resolveExamOrderEditorBody = (row: any): string => {
    const tpl = normalizeExamTemplateRow(row);
    const raw =
      typeof tpl.body_text === 'string' && tpl.body_text.trim()
        ? tpl.body_text
        : buildExamOrderTemplateBodyText(tpl);
    return formatExamOrderBodyWithoutNumbering(raw);
  };

  const loadExamOrderTemplatesForPicker = useCallback(async () => {
    setExamPickerLoading(true);
    try {
      const { data } = await axios.get(getApiUrl('/clinic/exam-orders/templates'));
      const list = Array.isArray(data?.data) ? data.data : data;
      setExamPickerRows(Array.isArray(list) ? list : []);
    } catch {
      setExamPickerRows([]);
    } finally {
      setExamPickerLoading(false);
    }
  }, []);

  const resolveExamTemplateBody = async (row: any): Promise<string> => {
    let source = normalizeExamTemplateRow(row);
    if (row.id) {
      try {
        const { data } = await axios.get(getApiUrl(`/clinic/exam-orders/templates/${row.id}`));
        const full = data?.data || data;
        if (full) source = normalizeExamTemplateRow(full);
      } catch {
        // Mantém dados da listagem se o detalhe falhar.
      }
    }
    return resolveExamOrderEditorBody(source);
  };

  const openEditor = async (row: any) => {
    const name = row.name || row.title || 'Modelo';
    if (row.isFree) {
      setEditorSession({ id: undefined, name, body: '' });
      return;
    }

    setEditorLoading(true);
    try {
      let source = normalizeExamTemplateRow(row);

      if (category === 'exam_order' && row.id) {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/exam-orders/templates/${row.id}`));
          const full = data?.data || data;
          if (full) source = normalizeExamTemplateRow(full);
        } catch {
          // Mantém dados da listagem se o detalhe falhar.
        }
      }

      let body = '';
      if (category === 'exam_order') {
        body = resolveExamOrderEditorBody(source);
      } else if (typeof source.body_text === 'string' && source.body_text.trim()) {
        body = source.body_text;
      }

      setEditorSession({ id: row.id, name, body });
    } finally {
      setEditorLoading(false);
    }
  };

  const appendExamTemplateToEditor = async (row: any) => {
    setEditorLoading(true);
    try {
      const addition = await resolveExamTemplateBody(row);
      const templateLabel = row.name || row.title || 'Exame';
      setEditorSession((prev) => {
        if (!prev) return prev;
        const mergedBody = mergeExamOrderEditorBodies(prev.body, addition);
        const alreadyCombined = prev.name.includes(' + ');
        const nextName = alreadyCombined
          ? `${prev.name} + ${templateLabel}`
          : `${prev.name} + ${templateLabel}`;
        return { ...prev, name: nextName, body: mergedBody };
      });
    } finally {
      setEditorLoading(false);
    }
  };

  const commitEditor = () => {
    if (!editorSession) return;
    const item: EvolutionAttachedPrintable = {
      clientKey: crypto.randomUUID(),
      category,
      templateId: editorSession.id,
      templateName: editorSession.name,
      bodyText: editorSession.body.trim(),
    };
    enqueueToAssistant(item);
    setEditorSession(null);
  };

  const handleReorderExamOrderTemplates = async (orderedIds: string[]) => {
    setExamPickerRows((prev) => {
      const byId = new Map(prev.map((r) => [String(r.id), r]));
      return orderedIds.map((id) => byId.get(id)).filter(Boolean) as any[];
    });
    try {
      await axios.put(getApiUrl('/clinic/exam-orders/templates/reorder'), { ids: orderedIds });
    } catch {
      void loadExamOrderTemplatesForPicker();
    }
  };

  const handleReorderModels = async (orderedIds: string[]) => {
    // Atualização otimista da ordem exibida.
    setRows((prev) => {
      const free = prev.filter((r) => r.isFree);
      const byId = new Map(prev.map((r) => [r.id, r]));
      const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean) as any[];
      return [...free, ...reordered];
    });
    try {
      await axios.put(getApiUrl('/clinic/document-text-templates/reorder'), { ids: orderedIds });
    } catch {
      loadTemplates();
    }
  };

  const handleDeleteModel = async (id: string) => {
    try {
      await axios.delete(getApiUrl(`/clinic/document-text-templates/${id}`));
    } finally {
      loadTemplates();
    }
  };

  const handleClinicalSuccess = (item: EvolutionAttachedPrintable) => {
    setPrescriptionDialogOpen(false);
    setCertificateDialogOpen(false);
    setMedicalReportDialogOpen(false);
    setPrefillRxTemplate(null);
    setPrefillCertTemplate(null);
    setPrefillMedicalReportTemplate(null);
    // Receituário/atestado vão direto para os impressos da evolução (1 clique),
    // levando junto qualquer item já enfileirado — sem etapa extra de confirmação.
    setAssistantQueue((q) => {
      commitPrintables([...q, item]);
      return [];
    });
    onClose();
  };

  const handlePrescriptionSuccess = async (result: PrescriptionFormSuccessResult) => {
    setPrescriptionDialogOpen(false);
    setPrefillRxTemplate(null);
    setAssistantQueue((q) => {
      commitPrintables([...q, ...result.printables]);
      return [];
    });

    if (result.completion === 'print') {
      try {
        const queue = await buildPrescriptionPrintQueueFromIds(
          async (id) => {
            const res = await axios.get(getApiUrl(`/clinic/prescriptions/${id}/print`));
            return res.data?.data ?? null;
          },
          result.savedPrescriptions,
        );
        if (queue.length) {
          setRxBatchPrintFirstVia(Boolean(result.printFirstViaOnly));
          setRxBatchPrintQueue(queue);
          setRxBatchPrintOpen(true);
        }
      } catch {
        /* impressão opcional após anexar à evolução */
      }
      onClose();
      return;
    }

    if (result.completion === 'sign') {
      const ids = result.savedPrescriptions.map((saved) => saved?.id).filter(Boolean) as string[];
      if (ids.length) {
        setSignAndSendIds(ids);
        onClose();
        return;
      }
    }

    onClose();
  };

  const openPrescriptionFlow = (tpl: any | null) => {
    setPrefillRxTemplate(tpl);
    setPrescriptionDialogOpen(true);
  };

  /** A receita já foi persistida pelo backend; aqui só a anexamos à evolução. */
  const handleMemedPrescriptionSaved = (prescription: any) => {
    if (!prescription?.id) return;
    handleClinicalSuccess({
      clientKey: crypto.randomUUID(),
      category: 'prescription',
      templateName: `Receita digital Memed${
        prescription.prescription_number ? ` ${prescription.prescription_number}` : ''
      }`,
      bodyText: buildMemedPrescriptionSummary(prescription),
      linkedDocumentId: prescription.id,
      linkedDocumentType: 'prescription',
      linkedDocumentOrigin: 'memed',
    });
  };

  const openCertificateFlow = (tpl: any | null) => {
    setPrefillCertTemplate(tpl);
    setCertificateDialogOpen(true);
  };

  const openMedicalReportFlow = (tpl: any | null) => {
    setPrefillMedicalReportTemplate(tpl);
    setMedicalReportDialogOpen(true);
  };

  const catInfo = CATEGORY_DEF.find((c) => c.key === category);

  const isOphthalmologyExamFlow =
    category === 'exam_order' && clinicSettingsLoaded && clinicMedicalSpecialty === 'oftalmologia';

  const isOphthalmologyRefractionFlow =
    category === 'refraction' && clinicSettingsLoaded && clinicMedicalSpecialty === 'oftalmologia';

  const showSimpleTemplateList =
    category === 'exam_order' ||
    category === 'report' ||
    category === 'surgical_risk' ||
    category === 'insurance_guide';

  /** Monta o corpo de uma guia de convênio (paciente + TUSS + quantidade + texto). */
  const openGuide = (form: any) => {
    const lines: string[] = [];
    const pname = initialPatient?.name || initialPatient?.full_name;
    if (pname) lines.push(`Paciente: ${pname}`);
    if (form.tuss_code) lines.push(`Código TUSS: ${form.tuss_code}`);
    lines.push(`Quantidade: ${form.quantity || 1}`);
    if (form.body_text && String(form.body_text).trim()) {
      lines.push('');
      lines.push(String(form.body_text));
    }
    setEditorSession({ id: undefined, name: form.name || 'Guia de convênio', body: lines.join('\n') });
  };

  const commitOphthalmologyExamOrder = () => {
    const bodyText = buildOphthalmologyExamOrderBody(ophSelections, ophOutros, ophObs);
    const item: EvolutionAttachedPrintable = {
      clientKey: crypto.randomUUID(),
      category: 'exam_order',
      templateName: 'Solicitação de exames — Oftalmologia',
      bodyText,
    };
    enqueueToAssistant(item);
    setOphSelections(emptyOphthalmologySelections());
    setOphOutros('');
    setOphObs('');
  };

  const commitRefraction = () => {
    const item: EvolutionAttachedPrintable = {
      clientKey: crypto.randomUUID(),
      category: 'refraction',
      templateName: 'Prescrição de refração',
      bodyText: serializeRefractionBody(refractionValues),
    };
    enqueueToAssistant(item);
    setRefractionValues(emptyOphthalmologyRefraction());
  };

  const openExamOrderFreeText = () => openEditor({
    id: 'free-exam_order',
    isFree: true,
    name: 'Solicitação em texto livre',
  });

  const renderExamOrderModelsSection = () => (
    <Box sx={{ mb: isOphthalmologyExamFlow ? 0 : 1 }}>
      {examPickerLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
        </Box>
      ) : examPickerRows.length > 0 ? (
        <PrescriptionTemplateChipPicker
          templates={examPickerRows}
          onSelect={(row) => void openEditor({ ...row, name: row.name || row.title || 'Modelo' })}
          onReorder={handleReorderExamOrderTemplates}
          title="Modelos pré-preenchidos"
          searchPlaceholder="Pesquisar solicitação de exame..."
          emptySearchMessage={(q) => `Nenhum modelo encontrado para "${q}".`}
        />
      ) : (
        <Typography variant="body2" sx={{ color: '#94a3b8', py: 1 }}>
          Nenhum modelo cadastrado. Cadastre em Pedidos de Exames › Modelos pré-preenchidos.
        </Typography>
      )}
      <Button
        fullWidth
        variant="outlined"
        disabled={editorLoading}
        onClick={openExamOrderFreeText}
        sx={{
          mt: 2,
          py: 1,
          textTransform: 'none',
          fontWeight: 700,
          borderColor: '#cbd5e1',
          color: '#475569',
          '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
        }}
      >
        Solicitação em texto livre
      </Button>
    </Box>
  );

  const renderPrefilledModelsSection = () => (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        Modelos pré-preenchidos
      </Typography>
      <ModelTemplateSearchField
        value={search}
        onChange={setSearch}
        placeholder={
          category === 'insurance_guide'
            ? 'Pesquisar guia...'
            : 'Pesquisar modelo...'
        }
      />
      {loading || editorLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
        </Box>
      ) : filteredRows.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#94a3b8', p: 2 }}>
          {search.trim()
            ? `Nenhum modelo encontrado para "${search.trim()}".`
            : category === 'insurance_guide'
              ? initialPatient?.insurance_id
                ? 'Nenhuma guia cadastrada para o convênio deste paciente. Cadastre em Configurações da Clínica › Guias por convênio.'
                : 'Paciente sem convênio vinculado. As guias aparecem aqui quando o paciente tem um convênio.'
              : 'Nenhum modelo encontrado.'}
        </Typography>
      ) : reorderMode && (category === 'report' || category === 'surgical_risk') ? (
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
            Arraste pelos pontos para reordenar seus modelos.
          </Typography>
          <SortableModelList
            items={filteredRows
              .filter((r) => !r.isFree)
              .map((r) => ({ id: String(r.id), name: r.name || r.title || 'Modelo' }))}
            onReorder={handleReorderModels}
            onDelete={handleDeleteModel}
          />
        </Box>
      ) : (
        <List dense disablePadding>
          {filteredRows.map((row, idx) => {
            const name = row.name || row.title || `Modelo ${idx + 1}`;
            return (
              <ListItemButton
                key={row.id || idx}
                disabled={editorLoading}
                onClick={() => (row.isInsuranceForm ? openGuide(row) : openEditor({ ...row, name }))}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  py: 1.25,
                  '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.06)' },
                }}
              >
                <ListItemText
                  primary={name}
                  secondary={
                    row.isInsuranceForm
                      ? [row.tuss_code ? `TUSS ${row.tuss_code}` : null, `Qtd ${row.quantity || 1}`]
                          .filter(Boolean)
                          .join(' · ')
                      : row.description
                          ? String(row.description).slice(0, 80)
                          : undefined
                  }
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
                <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                  Abrir
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      )}
    </>
  );

  return (
    <>
      <Dialog
        open={open && !editorSession && !prescriptionDialogOpen && !docTemplateDialogOpen && !certificateDialogOpen && !medicalReportDialogOpen}
        onClose={discardWizardAndClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#f8fafc',
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: 0.2 }}>
              Impressos e solicitações
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GuidanceButton title="Como usar este assistente">
              Por tipo à esquerda, use &quot;Adicionar à fila&quot;. Você pode acumular receituário, exames, laudos etc.;
              as <strong>indicações cirúrgicas/de procedimento</strong> ficam na seção própria da evolução (fora deste assistente).
              Ao final confira a fila abaixo e confirme uma vez em &quot;Adicionar à evolução&quot;.
            </GuidanceButton>
            <Tooltip title="Configurações dos modelos (cadastro da clínica)">
              <IconButton size="small" onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={{ color: '#64748b' }}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={discardWizardAndClose} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)}>
          <MenuItem disabled>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            Timbrado / papel: configure em Configurações da clínica
          </MenuItem>
          <MenuItem disabled>
            <ListItemIcon><AssignmentIcon fontSize="small" /></ListItemIcon>
            Modelos de receituário e atestado: menus Receituários e Atestados
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSettingsAnchor(null);
              window.open('/clinic/exam-orders', '_blank');
            }}
          >
            <ListItemIcon><ScienceIcon fontSize="small" /></ListItemIcon>
            Modelos de pedido de exame
          </MenuItem>
        </Menu>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 380, maxHeight: { xs: '75vh', sm: '70vh' }, overflow: 'hidden' }}>
          <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          <List
            component="nav"
            sx={{
              width: 228,
              flexShrink: 0,
              py: 1,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fafafa',
            }}
          >
            {visibleCategories.map((c) => {
              const Ico = c.icon;
              const sel = category === c.key;
              return (
                <ListItemButton
                  key={c.key}
                  selected={sel}
                  onClick={() => setCategory(c.key)}
                  sx={{
                    py: 1.25,
                    px: 2,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(59, 130, 246, 0.12)',
                      borderLeft: '3px solid #3b82f6',
                    },
                  }}
                >
                  <Ico sx={{ fontSize: 20, mr: 1.5, color: sel ? '#1d4ed8' : '#64748b' }} />
                  <ListItemText
                    primary={c.label}
                    primaryTypographyProps={{
                      fontWeight: sel ? 700 : 500,
                      fontSize: '0.8125rem',
                      color: sel ? '#0f172a' : '#475569',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#3b82f6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  {catInfo?.label}
                </Typography>
                {isOphthalmologyExamFlow && (
                  <GuidanceButton title="Solicitação de exames — Oftalmologia">
                    Clínica configurada como <strong>Oftalmologia</strong>: use os <strong>modelos pré-cadastrados</strong> no topo
                    ou a grade OD / OE / AO para montar a solicitação oftalmológica.
                  </GuidanceButton>
                )}
                {isOphthalmologyRefractionFlow && (
                  <GuidanceButton title="Prescrição de refração">
                    Preencha as caixinhas do grau (esférico, cilíndrico, eixo e adição para perto) e as observações.
                    O documento entra na fila com os demais impressos para salvar e imprimir junto da evolução.
                  </GuidanceButton>
                )}
              </Box>
              {(category === 'prescription' || category === 'certificate' || category === 'medical_report') && !hasClinicalContext && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                  Selecione <strong>paciente</strong> e <strong>profissional</strong> na identificação da evolução para emitir
                  {category === 'prescription'
                    ? ' receituário'
                    : category === 'certificate'
                      ? ' atestado'
                      : ' relatório médico'}{' '}
                  com o cadastro completo.
                </Alert>
              )}
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
              {category === 'prescription' && hasClinicalContext && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openPrescriptionFlow(null)}
                    sx={{
                      py: 1.25,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#3b82f6',
                      '&:hover': { bgcolor: '#2563eb' },
                    }}
                  >
                    Novo receituário
                  </Button>
                  <Typography variant="caption" sx={{ color: '#64748b', textAlign: 'center', px: 0.5 }}>
                    Assinatura digital ICP-Brasil com certificado A1 ou A3 do sistema
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                    <MemedPrescriptionButton
                      prominence="subtle"
                      patientId={patientId}
                      professionalId={professionalId}
                      onSaved={handleMemedPrescriptionSaved}
                    />
                  </Box>
                </Box>
              )}

              {category === 'certificate' && hasClinicalContext && (
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openCertificateFlow(null)}
                    sx={{
                      py: 1.25,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#3b82f6',
                      '&:hover': { bgcolor: '#2563eb' },
                    }}
                  >
                    Novo atestado
                  </Button>
                </Box>
              )}

              {category === 'medical_report' && hasClinicalContext && (
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openMedicalReportFlow(null)}
                    sx={{
                      py: 1.25,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#0d9488',
                      '&:hover': { bgcolor: '#0f766e' },
                    }}
                  >
                    Novo relatório médico
                  </Button>
                  <Typography variant="caption" sx={{ display: 'block', color: '#64748b', textAlign: 'center', mt: 1.5, px: 0.5 }}>
                    O relatório é salvo no prontuário do paciente e entra nos impressos desta evolução.
                  </Typography>
                </Box>
              )}

              {category === 'exam_order' && !clinicSettingsLoaded && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
                </Box>
              )}

              {category === 'exam_order' && clinicSettingsLoaded && (
                <>
                  {renderExamOrderModelsSection()}
                  {isOphthalmologyExamFlow ? <Divider sx={{ my: 2 }} /> : null}
                </>
              )}

              {category === 'refraction' && !clinicSettingsLoaded && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
                </Box>
              )}

              {isOphthalmologyExamFlow && (
                <OphthalmologyExamOrderSection
                  selections={ophSelections}
                  onChangeSelections={setOphSelections}
                  outrosText={ophOutros}
                  onChangeOutros={setOphOutros}
                  observacoes={ophObs}
                  onChangeObservacoes={setOphObs}
                  onCommit={commitOphthalmologyExamOrder}
                />
              )}

              {isOphthalmologyRefractionFlow && (
                <OphthalmologyRefractionSection
                  values={refractionValues}
                  onChange={setRefractionValues}
                  onCommit={commitRefraction}
                />
              )}

              {category === 'refraction' && clinicSettingsLoaded && !isOphthalmologyRefractionFlow && (
                <Typography variant="body2" sx={{ color: '#94a3b8', py: 3 }}>
                  A prescrição de refração fica disponível quando a clínica está configurada como oftalmologia.
                </Typography>
              )}

              {showSimpleTemplateList && category !== 'exam_order' && (category === 'report' || category === 'surgical_risk') && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setDocTemplateDialogOpen(true)}
                    sx={{
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderColor: '#0284c7',
                      color: '#0284c7',
                      '&:hover': { borderColor: '#0369a1', bgcolor: '#f0f9ff' },
                    }}
                  >
                    Cadastrar novo modelo de {category === 'report' ? 'laudo' : 'risco cirúrgico'}
                  </Button>
                  {filteredRows.some((r) => !r.isFree) && (
                    <Button
                      variant={reorderMode ? 'contained' : 'text'}
                      startIcon={<DragIndicatorIcon />}
                      onClick={() => setReorderMode((v) => !v)}
                      sx={{
                        py: 1,
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                        fontWeight: 700,
                        ...(reorderMode
                          ? { bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }
                          : { color: '#64748b' }),
                      }}
                    >
                      {reorderMode ? 'Concluir' : 'Reordenar'}
                    </Button>
                  )}
                </Box>
              )}

              {showSimpleTemplateList && category !== 'exam_order' && renderPrefilledModelsSection()}

              {(category === 'prescription' || category === 'certificate') && !hasClinicalContext && (
                <Typography variant="body2" sx={{ color: '#94a3b8', py: 3 }}>
                  Preencha a identificação do atendimento na evolução para continuar.
                </Typography>
              )}
            </Box>
          </Box>
          </Box>
          {assistantQueue.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: '#ecfdf5',
                px: 2,
                py: 1.25,
                maxHeight: 148,
                overflowY: 'auto',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#1d4ed8', display: 'block', mb: 0.75 }}>
                Na fila ({assistantQueue.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {assistantQueue.map((printable) => (
                  <Box
                    key={printable.clientKey}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'space-between',
                      bgcolor: '#fff',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      border: '1px solid #a7f3d0',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        color: '#334155',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      <Box component="span" sx={{ color: '#2563eb', mr: 0.75 }}>
                        {categoryLabel(printable.category)}
                      </Box>
                      {printable.templateName}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Remover da fila"
                      onClick={() => removeFromAssistantQueue(printable.clientKey)}
                      sx={{ flexShrink: 0 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa', gap: 1 }}
        >
          <Button onClick={discardWizardAndClose} sx={{ textTransform: 'none', color: '#64748b' }}>
            {assistantQueue.length > 0 ? 'Descartar fila e fechar' : 'Fechar'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            disabled={assistantQueue.length === 0}
            onClick={flushAssistantQueueAndClose}
            startIcon={<CheckCircleIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' },
            }}
          >
            Adicionar à evolução{assistantQueue.length ? ` (${assistantQueue.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <PrescriptionFormDialog
        open={prescriptionDialogOpen}
        onClose={() => {
          setPrescriptionDialogOpen(false);
          setPrefillRxTemplate(null);
        }}
        patientId={patientId}
        professionalId={professionalId}
        initialPatient={initialPatient}
        initialProfessional={initialProfessional}
        prefillTemplate={prefillRxTemplate}
        onSuccess={handlePrescriptionSuccess}
      />

      <EvolutionPrescriptionsSendDialog
        open={signAndSendIds.length > 0}
        mode="sign_then_send"
        prescriptionIds={signAndSendIds}
        patientId={patientId}
        onClose={() => setSignAndSendIds([])}
      />

      <PrescriptionBatchPrintDialog
        open={rxBatchPrintOpen}
        onClose={() => setRxBatchPrintOpen(false)}
        items={rxBatchPrintQueue}
        initialFirstViaOnly={rxBatchPrintFirstVia}
      />

      {(category === 'report' || category === 'surgical_risk') && (
        <DocumentTextTemplateFormDialog
          open={docTemplateDialogOpen}
          onClose={() => setDocTemplateDialogOpen(false)}
          category={category}
          professionalId={professionalId}
          onSaved={() => { setDocTemplateDialogOpen(false); loadTemplates(); }}
        />
      )}

      <CertificateFormDialog
        open={certificateDialogOpen}
        onClose={() => {
          setCertificateDialogOpen(false);
          setPrefillCertTemplate(null);
        }}
        patientId={patientId}
        professionalId={professionalId}
        initialPatient={initialPatient}
        initialProfessional={initialProfessional}
        prefillTemplate={prefillCertTemplate}
        onSuccess={handleClinicalSuccess}
      />

      <HealthcareMedicalReportDialog
        open={medicalReportDialogOpen}
        onClose={() => {
          setMedicalReportDialogOpen(false);
          setPrefillMedicalReportTemplate(null);
        }}
        patientId={patientId}
        patientName={initialPatient?.full_name || initialPatient?.name || 'Paciente'}
        professionalId={professionalId}
        initialProfessional={initialProfessional}
        prefillTemplate={prefillMedicalReportTemplate}
        onSuccess={handleClinicalSuccess}
      />

      <Dialog
        open={Boolean(editorSession)}
        onClose={() => { setEditorSession(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {editorSession?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {category === 'exam_order'
                ? 'Escolha modelos no topo para combinar exames; revise o texto abaixo antes de adicionar à fila.'
                : 'O texto será colocado na fila do assistente; você pode acumular outros documentos antes de confirmar na evolução.'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => { setEditorSession(null); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          {category === 'exam_order' && editorSession && examPickerRows.length > 0 && (
            <>
              <PrescriptionTemplateChipPicker
                templates={examPickerRows}
                onSelect={(row) => void appendExamTemplateToEditor(row)}
                onReorder={handleReorderExamOrderTemplates}
                title="Modelos pré-preenchidos"
                searchPlaceholder="Pesquisar exame..."
                emptySearchMessage={(q) => `Nenhum modelo encontrado para "${q}".`}
              />
              <Divider sx={{ mb: 2 }} />
            </>
          )}
          {editorSession && !editorSession.body.trim() && category === 'exam_order' ? (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Este modelo não retornou exames. Verifique em Pedidos de Exames › Modelos pré-preenchidos se há itens cadastrados.
            </Alert>
          ) : null}
          {editorSession ? (
            <ClinicalHtmlEditor
              key={editorSession.id || editorSession.name}
              minRows={12}
              placeholder="Digite pedido de exame, laudo, risco cirúrgico..."
              value={editorSession.body}
              onChange={(body) => setEditorSession((prev) => (prev ? { ...prev, body } : null))}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
          <Button onClick={() => { setEditorSession(null); }} sx={{ textTransform: 'none' }}>
            Voltar
          </Button>
          <Button
            variant="contained"
            onClick={commitEditor}
            disabled={!editorSession || clinicalBodyIsEmpty(editorSession.body)}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            Adicionar à fila
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EvolutionPrintablesWizard;
